import { memo } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import PercentIcon from "@mui/icons-material/Percent";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";

import IconInfoCard from "src-next/shared/ui/cards/IconInfoCard";

import type {
  CrmQuestionnaireLatestResponseItem,
  CrmQuestionnaireUsageStats,
} from "../types";

export interface CrmQuestionnaireStatisticsCardsProps {
  questionnaireUsageStats: CrmQuestionnaireUsageStats | null;
  questionnaireLatestResponses: CrmQuestionnaireLatestResponseItem[];
  onOpenLatestQuestionnaireResponsesDialog: () => void;
}

const CrmQuestionnaireStatisticsCards = memo<CrmQuestionnaireStatisticsCardsProps>(
  ({
    questionnaireUsageStats,
    questionnaireLatestResponses,
    onOpenLatestQuestionnaireResponsesDialog,
  }) => {
    const sends = questionnaireUsageStats?.totalSends ?? 0;
    const responses = questionnaireUsageStats?.totalResponses ?? 0;
    const rate = questionnaireUsageStats?.averageResponseRateInPercentage ?? 0;

    return (
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(2, minmax(0, 1fr))",
            xl: "repeat(3, minmax(0, 1fr))",
          },
        }}
      >
        <IconInfoCard
          icon={<QueryStatsIcon />}
          title="Envios e respostas"
          color="primary"
          sx={{ minHeight: 220 }}
        >
          <Stack spacing={1}>
            <Typography variant="h4" fontWeight={700}>
              {sends.toLocaleString("pt-BR")} / {responses.toLocaleString("pt-BR")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total de envios e respostas recebidas (mock).
            </Typography>
          </Stack>
        </IconInfoCard>

        <IconInfoCard
          icon={<PercentIcon />}
          title="Taxa média de respostas"
          color="success"
          sx={{ minHeight: 220 }}
        >
          <Stack spacing={1.25}>
            <Typography variant="h4" fontWeight={700}>
              {`${rate}%`}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={rate}
              sx={{ height: 8, borderRadius: 999 }}
            />
            <Typography variant="body2" color="text.secondary">
              Média calculada a partir dos envios e respostas (mock).
            </Typography>
          </Stack>
        </IconInfoCard>

        <IconInfoCard
          icon={<ForumOutlinedIcon />}
          title="Últimas respostas"
          color="secondary"
          sx={{ minHeight: 220 }}
        >
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Itens recentes: {questionnaireLatestResponses.length}
            </Typography>
            <Button
              variant="contained"
              size="small"
              sx={{ alignSelf: "flex-start", textTransform: "none" }}
              onClick={onOpenLatestQuestionnaireResponsesDialog}
            >
              Ver últimas respostas
            </Button>
          </Stack>
        </IconInfoCard>
      </Box>
    );
  },
);

CrmQuestionnaireStatisticsCards.displayName =
  "CrmQuestionnaireStatisticsCards";

export default CrmQuestionnaireStatisticsCards;

